import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE,
    DEFAULT_LANG,
    YES, NO,
} from "const"
import { CcResponse, CcResponseCreate } from "server/models"
import { deepCopy, StateType, YesNo } from "client/utils"
import {
    StoreType,
    CcResponseTranslation,
    CcResponseValidation,
    SaveCcResponse,
    closeModal,
    setCcResponseEdit,
    setCcResponseReloadList,
    setCcResponseReloadEdit,
    LangType,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Feedback,
    RadioButton,
    Button,
    SpinnerIcon,
} from "client/components"

const langId = "en" // display translation only for English, hide others

interface ReponseEditProps {
    response: CcResponse
    module: number
}

let isMounted: boolean

const Form = ({
    response,
    module,
}: ReponseEditProps) => {
    const project = useSelector((state: StoreType) => state.project.projectView)

    const modals = useSelector((state: StoreType) => state.system.modals)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [translations, setTranslations] = useState<CcResponseTranslation>(
        response.translations ? JSON.parse(response.translations) : {},
    )
    const [approved, setApproved] = useState<YesNo>(module === 0 ? null : response.approved)
    const [validation, setValidation] = useState<CcResponseValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (modals.length && isMounted) dispatch(closeModal())
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newResponseData: CcResponseCreate = {
            accountId: response.accountId || userData.accountId,
            projectId: response.projectId || project.id,
            response: translations[DEFAULT_LANG],
            approved,
            translations: JSON.stringify(translations),
            creator: response.id ? response.creator : userData.userId,
            module,
            order: response.id ? response.order : 0,
        }

        SaveCcResponse(response.id, newResponseData, lang)
        .then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)
            }
            if (res.validation) return

            dispatch(setCcResponseEdit({
                ...newResponseData,
                id: res.newResponseId || response.id,
                order: res.newResponseOrder || response.order,
            }))

            dispatch(setCcResponseReloadList(false))
            setTimeout(() => {
                dispatch(setCcResponseReloadList(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setCcResponseReloadEdit(false))
                setTimeout(() => {
                    dispatch(setCcResponseReloadEdit(true))
                }, 0)
            }
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup key={langId}>
                        <Label>{lang[langId as keyof LangType]}</Label>
                        <Input
                            type="text"
                            value={translations[langId] || ""}
                            onChange={(e) => {
                                const translationsCopy: CcResponseTranslation = deepCopy(translations)
                                translationsCopy[langId] = e.target.value
                                setTranslations(translationsCopy)
                            }}
                        />
                        {
                            langId === DEFAULT_LANG
                            && <Feedback>{validation && validation.response}</Feedback>
                        }
                    </FormGroup>

                    {
                        module !== 0 && (
                            <FormGroup>
                                <Label>{lang.approved}</Label>
                                <FormGroup inline>
                                    <RadioButton
                                        checked={approved === YES}
                                        onChange={() => setApproved(YES)}
                                        title={lang.yes}
                                    />
                                    <RadioButton
                                        checked={approved === NO}
                                        onChange={() => setApproved(NO)}
                                        title={lang.no}
                                    />
                                </FormGroup>
                            </FormGroup>
                        )
                    }
                </Col>
                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
